import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, IconButton, Dialog, DialogTitle, DialogContent, Grid, Chip, TextField, TableSortLabel, Menu, MenuItem } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api, { API_BASE_URL } from '../services/api';
import { fetchCandidatesForOffer, RecruitCandidateRow } from '../services/recruitmentService';

const CandidatesForOfferPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const offerId = Number(id);
  const [rows, setRows] = React.useState<RecruitCandidateRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [sortField, setSortField] = React.useState<'id'|'full_name'|'email'|'phone'|'iq_percent'|'stress_percent'|'stage'|'rating_cv'|'rating_interview'>('id');
  const [sortDir, setSortDir] = React.useState<'asc'|'desc'>('asc');
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewData, setPreviewData] = React.useState<any>(null);
  const [rankings, setRankings] = React.useState<Record<string, number>>({});
  const [stages, setStages] = React.useState<Record<string, 'I'|'II'|'III'|''>>({});
  const [interviewRatings, setInterviewRatings] = React.useState<Record<string, number>>({});
  const [stageMenuAnchor, setStageMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [stageMenuRowId, setStageMenuRowId] = React.useState<number | null>(null);
  const [ratingCvMenuAnchor, setRatingCvMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [ratingCvMenuRowId, setRatingCvMenuRowId] = React.useState<number | null>(null);
  const [ratingInterviewMenuAnchor, setRatingInterviewMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [ratingInterviewMenuRowId, setRatingInterviewMenuRowId] = React.useState<number | null>(null);
  const filePreviewUrl = React.useMemo(() => {
    if (!previewData?.candidate?.id) return '';
    const base = (API_BASE_URL || '').replace(/\/?api$/, '');
    return `${base}/api/recruit/candidates/${previewData.candidate.id}/cv`;
  }, [previewData]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchCandidatesForOffer(offerId);
        if (!mounted) return; setRows(data || []);
      } catch {}
      finally { setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [offerId]);

  // Load persisted sort on offer change
  React.useEffect(()=>{
    const key = `offer_${offerId}_candidates_sort`;
    try{
      const raw = localStorage.getItem(key);
      if(raw){
        const parsed = JSON.parse(raw) as { field?: string; dir?: 'asc'|'desc' } | null;
        const allowedFields: any = { id:1, full_name:1, email:1, phone:1, iq_percent:1, stress_percent:1, stage:1, rating_cv:1, rating_interview:1 };
        if(parsed?.field && allowedFields[parsed.field]) setSortField(parsed.field as any);
        if(parsed?.dir === 'asc' || parsed?.dir === 'desc') setSortDir(parsed.dir);
      }
    } catch{}
  }, [offerId]);

  // Persist sort on change
  React.useEffect(()=>{
    const key = `offer_${offerId}_candidates_sort`;
    try{ localStorage.setItem(key, JSON.stringify({ field: sortField, dir: sortDir })); } catch{}
  }, [offerId, sortField, sortDir]);

  // Ranking: load/save from localStorage per offer
  React.useEffect(()=>{
    const key = `offer_${offerId}_candidate_rankings`;
    try{
      const raw = localStorage.getItem(key);
      if(raw){ setRankings(JSON.parse(raw) || {}); }
    } catch{}
  }, [offerId]);

  const saveRankings = React.useCallback((next: Record<string, number>)=>{
    const key = `offer_${offerId}_candidate_rankings`;
    try{ localStorage.setItem(key, JSON.stringify(next)); } catch{}
  }, [offerId]);

  // Interview ratings load/save
  React.useEffect(()=>{
    const key = `offer_${offerId}_candidate_interview_ratings`;
    try{
      const raw = localStorage.getItem(key);
      if(raw){ setInterviewRatings(JSON.parse(raw) || {}); }
    } catch{}
  }, [offerId]);

  const saveInterviewRatings = React.useCallback((next: Record<string, number>)=>{
    const key = `offer_${offerId}_candidate_interview_ratings`;
    try{ localStorage.setItem(key, JSON.stringify(next)); } catch{}
  }, [offerId]);

  // Stage per offer
  React.useEffect(()=>{
    const key = `offer_${offerId}_candidate_stages`;
    try{
      const raw = localStorage.getItem(key);
      if(raw){ setStages(JSON.parse(raw) || {}); }
    } catch{}
  }, [offerId]);

  const saveStages = React.useCallback((next: Record<string, 'I'|'II'|'III'|''>)=>{
    const key = `offer_${offerId}_candidate_stages`;
    try{ localStorage.setItem(key, JSON.stringify(next)); } catch{}
  }, [offerId]);

  const filtered = React.useMemo(()=>{
    const s = search.trim().toLowerCase();
    if(!s) return rows;
    return rows.filter(r => (
      String(r.id).includes(s) ||
      (r.full_name || '').toLowerCase().includes(s) ||
      (r.email || '').toLowerCase().includes(s) ||
      (r.phone || '').toLowerCase().includes(s)
    ));
  }, [rows, search]);

  const sorted = React.useMemo(()=>{
    const arr = [...filtered];
    arr.sort((a,b)=>{
      let av: any; let bv: any;
      switch(sortField){
        case 'id': av=a.id; bv=b.id; break;
        case 'full_name': av=(a.full_name||'').toLowerCase(); bv=(b.full_name||'').toLowerCase(); break;
        case 'email': av=(a.email||'').toLowerCase(); bv=(b.email||'').toLowerCase(); break;
        case 'phone': av=(a.phone||'').toLowerCase(); bv=(b.phone||'').toLowerCase(); break;
        case 'iq_percent': av=(a.iq_percent ?? -1); bv=(b.iq_percent ?? -1); break;
        case 'stress_percent': av=(a.stress_percent ?? -1); bv=(b.stress_percent ?? -1); break;
        case 'stage': {
          const stageA = stages[String(a.id)] ?? ((a.iq_percent != null || a.stress_percent != null) ? 'I' : '');
          const stageB = stages[String(b.id)] ?? ((b.iq_percent != null || b.stress_percent != null) ? 'I' : '');
          const toNum = (s: ''|'I'|'II'|'III') => s === '' ? -1 : (s === 'I' ? 1 : s === 'II' ? 2 : 3);
          av = toNum(stageA); bv = toNum(stageB);
          break;
        }
        case 'rating_cv': {
          av = (rankings[String(a.id)] ?? -1);
          bv = (rankings[String(b.id)] ?? -1);
          break;
        }
        case 'rating_interview': {
          av = (interviewRatings[String(a.id)] ?? -1);
          bv = (interviewRatings[String(b.id)] ?? -1);
          break;
        }
      }
      if(av<bv) return sortDir==='asc' ? -1 : 1;
      if(av>bv) return sortDir==='asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortField, sortDir, stages, rankings, interviewRatings]);

  const handleSort = (field: typeof sortField) => {
    if(sortField===field){ setSortDir(prev=> prev==='asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Button onClick={()=> navigate(-1)} sx={{ mb: 2 }}>Powrót</Button>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Kandydaci — oferta #{offerId}</Typography>
      <Box sx={{ display:'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Szukaj... (ID, imię, email, telefon)"
          value={search}
          onChange={(e)=> setSearch(e.target.value)}
        />
      </Box>
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100 }}>
                <TableSortLabel active={sortField==='id'} direction={sortField==='id'?sortDir: 'asc'} onClick={()=>handleSort('id')}>ID</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField==='full_name'} direction={sortField==='full_name'?sortDir: 'asc'} onClick={()=>handleSort('full_name')}>Imię i nazwisko</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField==='email'} direction={sortField==='email'?sortDir: 'asc'} onClick={()=>handleSort('email')}>Adres e‑mail</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 160 }}>
                <TableSortLabel active={sortField==='phone'} direction={sortField==='phone'?sortDir: 'asc'} onClick={()=>handleSort('phone')}>Telefon</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 120 }}>
                <TableSortLabel active={sortField==='iq_percent'} direction={sortField==='iq_percent'?sortDir: 'asc'} onClick={()=>handleSort('iq_percent')}>Test IQ</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 120 }}>
                <TableSortLabel active={sortField==='stress_percent'} direction={sortField==='stress_percent'?sortDir: 'asc'} onClick={()=>handleSort('stress_percent')}>Stress Test</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 160 }}>
                <TableSortLabel active={sortField==='stage'} direction={sortField==='stage'?sortDir: 'asc'} onClick={()=>handleSort('stage')}>Etap</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 140 }}>
                <TableSortLabel active={sortField==='rating_cv'} direction={sortField==='rating_cv'?sortDir: 'asc'} onClick={()=>handleSort('rating_cv')}>Rating CV</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 160 }}>
                <TableSortLabel active={sortField==='rating_interview'} direction={sortField==='rating_interview'?sortDir: 'asc'} onClick={()=>handleSort('rating_interview')}>Rating rozmowa</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 60 }} align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{r.id}</TableCell>
                <TableCell>{r.full_name}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell>{r.phone || '—'}</TableCell>
                <TableCell>{r.iq_percent != null ? `${r.iq_percent}%` : '—'}</TableCell>
                <TableCell>{r.stress_percent != null ? `${r.stress_percent}%` : '—'}</TableCell>
                <TableCell>
                  {(() => {
                    const current = stages[String(r.id)] ?? ((r.iq_percent != null || r.stress_percent != null) ? 'I' : '');
                    const color = current === 'I' ? 'info' : current === 'II' ? 'warning' : current === 'III' ? 'error' : 'default';
                    return (
                      <Chip
                        label={current || '—'}
                        color={color as any}
                        variant={current ? 'filled' : 'outlined'}
                        size="small"
                        onClick={(e)=>{ setStageMenuAnchor(e.currentTarget); setStageMenuRowId(r.id); }}
                        sx={{ cursor:'pointer' }}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const val = rankings[String(r.id)];
                    const color = val == null ? 'default' : (val >= 8 ? 'success' : val >=5 ? 'warning' : 'error');
                    return (
                      <Chip
                        label={val != null ? String(val) : '—'}
                        color={color as any}
                        variant={val != null ? 'filled' : 'outlined'}
                        size="small"
                        onClick={(e)=>{ setRatingCvMenuAnchor(e.currentTarget); setRatingCvMenuRowId(r.id); }}
                        sx={{ cursor:'pointer' }}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const val = interviewRatings[String(r.id)];
                    const color = val == null ? 'default' : (val >= 8 ? 'success' : val >=5 ? 'warning' : 'error');
                    return (
                      <Chip
                        label={val != null ? String(val) : '—'}
                        color={color as any}
                        variant={val != null ? 'filled' : 'outlined'}
                        size="small"
                        onClick={(e)=>{ setRatingInterviewMenuAnchor(e.currentTarget); setRatingInterviewMenuRowId(r.id); }}
                        sx={{ cursor:'pointer' }}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={async()=>{
                    try {
                      const { data } = await api.get(`/recruit/job_offers/${offerId}/candidates/${r.id}/review`);
                      setPreviewData(data?.data || null);
                      setPreviewOpen(true);
                    } catch {}
                  }}><VisibilityIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!sorted.length && !loading && (
              <TableRow><TableCell colSpan={10}>Brak kandydatów.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menus */}
      <Menu
        anchorEl={stageMenuAnchor}
        open={Boolean(stageMenuAnchor)}
        onClose={()=>{ setStageMenuAnchor(null); setStageMenuRowId(null); }}
      >
        <MenuItem onClick={()=>{
          if(stageMenuRowId==null) return;
          setStages(prev=>{ const next: Record<string, ''|'I'|'II'|'III'> = { ...prev }; delete next[String(stageMenuRowId)]; saveStages(next); return next; });
          setStageMenuAnchor(null); setStageMenuRowId(null);
        }}><em>—</em></MenuItem>
        <MenuItem onClick={()=>{
          if(stageMenuRowId==null) return;
          setStages(prev=>{ const next: Record<string, ''|'I'|'II'|'III'> = { ...prev }; next[String(stageMenuRowId)] = 'I'; saveStages(next); return next; });
          setStageMenuAnchor(null); setStageMenuRowId(null);
        }}>I</MenuItem>
        <MenuItem onClick={()=>{
          if(stageMenuRowId==null) return;
          setStages(prev=>{ const next: Record<string, ''|'I'|'II'|'III'> = { ...prev }; next[String(stageMenuRowId)] = 'II'; saveStages(next); return next; });
          setStageMenuAnchor(null); setStageMenuRowId(null);
        }}>II</MenuItem>
        <MenuItem onClick={()=>{
          if(stageMenuRowId==null) return;
          setStages(prev=>{ const next: Record<string, ''|'I'|'II'|'III'> = { ...prev }; next[String(stageMenuRowId)] = 'III'; saveStages(next); return next; });
          setStageMenuAnchor(null); setStageMenuRowId(null);
        }}>III</MenuItem>
      </Menu>

      <Menu
        anchorEl={ratingCvMenuAnchor}
        open={Boolean(ratingCvMenuAnchor)}
        onClose={()=>{ setRatingCvMenuAnchor(null); setRatingCvMenuRowId(null); }}
      >
        <MenuItem onClick={()=>{
          if(ratingCvMenuRowId==null) return;
          setRankings(prev=>{ const next={...prev}; delete next[String(ratingCvMenuRowId)]; saveRankings(next); return next; });
          setRatingCvMenuAnchor(null); setRatingCvMenuRowId(null);
        }}><em>—</em></MenuItem>
        {Array.from({length:10}).map((_,i)=> (
          <MenuItem key={i+1} onClick={()=>{
            if(ratingCvMenuRowId==null) return;
            setRankings(prev=>{ const next={...prev, [String(ratingCvMenuRowId)]: i+1}; saveRankings(next); return next; });
            setRatingCvMenuAnchor(null); setRatingCvMenuRowId(null);
          }}>{i+1}</MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={ratingInterviewMenuAnchor}
        open={Boolean(ratingInterviewMenuAnchor)}
        onClose={()=>{ setRatingInterviewMenuAnchor(null); setRatingInterviewMenuRowId(null); }}
      >
        <MenuItem onClick={()=>{
          if(ratingInterviewMenuRowId==null) return;
          setInterviewRatings(prev=>{ const next={...prev}; delete next[String(ratingInterviewMenuRowId)]; saveInterviewRatings(next); return next; });
          setRatingInterviewMenuAnchor(null); setRatingInterviewMenuRowId(null);
        }}><em>—</em></MenuItem>
        {Array.from({length:10}).map((_,i)=> (
          <MenuItem key={i+1} onClick={()=>{
            if(ratingInterviewMenuRowId==null) return;
            setInterviewRatings(prev=>{ const next={...prev, [String(ratingInterviewMenuRowId)]: i+1}; saveInterviewRatings(next); return next; });
            setRatingInterviewMenuAnchor(null); setRatingInterviewMenuRowId(null);
          }}>{i+1}</MenuItem>
        ))}
      </Menu>

      <Dialog open={previewOpen} onClose={()=>setPreviewOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Podgląd kandydata</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2 }}>
            {/* Lewy panel: PDF CV */}
            <Box sx={{ width: { xs: '100%', md: '70%' } }}>
              <Box sx={{ border: '2px dashed #9e9e9e', borderRadius: 1, height: { xs: '70vh', md: '70vh' } }}>
                {filePreviewUrl ? (
                  <iframe title="cv" src={filePreviewUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                ) : (
                  <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'text.secondary' }}>Brak CV</Box>
                )}
              </Box>
            </Box>
            {/* Prawy panel: dane i odpowiedzi */}
            <Paper sx={{ p: 2, width: { xs: '100%', md: '30%' }, maxHeight: '70vh', overflowY: 'auto' }}>
              {previewData?.candidate && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{previewData.candidate.first_name} {previewData.candidate.last_name}</Typography>
                  <Typography variant="body2" color="text.secondary">{previewData.candidate.email}</Typography>
                  <Typography variant="body2" color="text.secondary">{previewData.candidate.phone || '—'}</Typography>
                  {previewData?.iq_attempt && (
                    <Box sx={{ mt: 1 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Punkty IQ</Typography><Typography>{previewData.iq_attempt.score_points}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Czas IQ</Typography><Typography>{previewData.iq_attempt.duration_seconds || '—'} s</Typography></Grid>
                      </Grid>
                    </Box>
                  )}
                  {previewData?.stress_attempt && (
                    <Box sx={{ mt: 1 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Punkty STRESS</Typography><Typography>{previewData.stress_attempt.score_points}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Czas STRESS</Typography><Typography>{previewData.stress_attempt.duration_seconds || '—'} s</Typography></Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}
              {/* Odpowiedzi IQ */}
              {Array.isArray(previewData?.iq_answers) && previewData.iq_answers.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Odpowiedzi — Test IQ</Typography>
                  <Box sx={{ display:'flex', flexDirection:'column', gap: 1 }}>
                    {previewData.iq_answers.map((a: any, idx: number) => (
                      <Paper key={`iq-${idx}`} variant="outlined" sx={{ p: 1.25 }}>
                        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{a.code ? `${a.code}: ` : ''}{a.text}</Typography>
                        {a.image_url && <img src={a.image_url} alt="q" style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 6 }} />}
                        <Box sx={{ display:'flex', flexDirection:'column', gap: 0.5 }}>
                          {(a.options || []).map((o: any) => {
                            const isCorrect = a.correct_option_key === o.option_key;
                            const isSelected = a.selected_option_key === o.option_key;
                            const color = isCorrect ? 'success.main' : (isSelected && !a.is_correct ? 'error.main' : 'text.primary');
                            return (
                              <Box key={o.option_key} sx={{ display:'flex', alignItems:'center', gap: 1 }}>
                                <Chip size="small" label={o.option_key} />
                                <Typography variant="body2" sx={{ color }}>{o.option_text}</Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </>
              )}

              {/* Odpowiedzi STRESS */}
              {Array.isArray(previewData?.stress_answers) && previewData.stress_answers.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Odpowiedzi — Test STRESS</Typography>
                  <Box sx={{ display:'flex', flexDirection:'column', gap: 1 }}>
                    {previewData.stress_answers.map((a: any, idx: number) => (
                      <Paper key={`stress-${idx}`} variant="outlined" sx={{ p: 1.25 }}>
                        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{a.code ? `${a.code}: ` : ''}{a.text}</Typography>
                        {a.image_url && <img src={a.image_url} alt="q" style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 6 }} />}
                        <Box sx={{ display:'flex', flexDirection:'column', gap: 0.5 }}>
                          {(a.options || []).map((o: any) => {
                            const isSelected = a.selected_option_key === o.option_key;
                            const color = isSelected ? (a.is_disqualifying ? 'error.main' : (a.is_preferred ? 'success.main' : 'primary.main')) : 'text.primary';
                            return (
                              <Box key={o.option_key} sx={{ display:'flex', alignItems:'center', gap: 1 }}>
                                <Chip size="small" label={o.option_key} />
                                <Typography variant="body2" sx={{ color }}>{o.option_text}</Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CandidatesForOfferPage;


